import { useMemo } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useFilm } from '../context/FilmContext';

/**
 * useScopedNav — pure URL builder hook that derives sidebar / nav URLs
 * from the persisted Workspace + Film context. Returns URL strings
 * only; it never navigates by itself. (Phase 1 — state plumbing.)
 *
 *   const { to, canAccess, reason } = useScopedNav();
 *   <Link to={to.calendar()}>Calendar</Link>
 *
 * URL patterns (W = currentWorkspaceId, F = currentFilmId):
 *
 *   | item       | no ctx       | ws only (W)            | ws + film (W + F)                       |
 *   |------------|--------------|------------------------|------------------------------------------|
 *   | dashboard  | /            | /workspaces/W          | /workspaces/W/films/F                    |
 *   | projects   | /workspaces  | /workspaces/W          | /workspaces/W                            |
 *   | calendar   | /calendar    | /workspaces/W/calendar | /workspaces/W/films/F/calendar           |
 *   | gear       | /gear        | /workspaces/W/gear     | /workspaces/W/films/F/gear               |
 *   | files (a)  | /files       | /files                 | /workspaces/W/films/F/files              |
 *   | scenes (b) | null         | null                   | /workspaces/W/films/F/scenes             |
 *   | shots  (b) | null         | null                   | /workspaces/W/films/F/shots              |
 *   | team   (c) | /team        | /workspaces/W/members  | /workspaces/W/members                    |
 *   | settings(d)| /settings    | /settings              | /settings                                |
 *
 *   (a) `/files` currently has no workspace-scoped route; the
 *       workspace-only state falls back to the account URL.
 *   (b) Scenes & shots are film-scoped only. Without a film selected
 *       the builder returns null and canAccess[item] is false with
 *       reason[item] === 'no_film_selected'.
 *   (c) The legacy account-level /team route remains for the no-ctx
 *       case. With a workspace, the link points at workspace members.
 *   (d) /settings is treated as an always-account-level route per the
 *       Phase 1 spec. Workspace settings (/workspaces/:id/settings) is
 *       intentionally not surfaced here; the UI in later phases can
 *       choose to override.
 *
 * The returned object is memoised on currentWorkspaceId + currentFilmId
 * so consumers can use referential equality in deps arrays.
 */
export function useScopedNav() {
  const { currentWorkspaceId } = useWorkspace();
  const { currentFilmId } = useFilm();

  return useMemo(() => {
    const wsId = currentWorkspaceId || null;
    // A film without a workspace is not a valid scope for the URL
    // builder. (FilmContext clears film when workspace clears, but we
    // guard here as well to keep the builder pure.)
    const filmId = wsId && currentFilmId ? currentFilmId : null;

    const hasWs = Boolean(wsId);
    const hasFilm = Boolean(filmId);

    const wsPrefix = hasWs ? `/workspaces/${wsId}` : '';
    const filmPrefix = hasFilm ? `/workspaces/${wsId}/films/${filmId}` : '';

    const to = {
      dashboard: () => (hasFilm ? filmPrefix : hasWs ? wsPrefix : '/'),
      projects: () => (hasWs ? wsPrefix : '/workspaces'),
      calendar: () =>
        hasFilm ? `${filmPrefix}/calendar` : hasWs ? `${wsPrefix}/calendar` : '/calendar',
      gear: () =>
        hasFilm ? `${filmPrefix}/gear` : hasWs ? `${wsPrefix}/gear` : '/gear',
      files: () => (hasFilm ? `${filmPrefix}/files` : '/files'),
      scenes: () => (hasFilm ? `${filmPrefix}/scenes` : null),
      shots: () => (hasFilm ? `${filmPrefix}/shots` : null),
      team: () => (hasWs ? `${wsPrefix}/members` : '/team'),
      settings: () => '/settings',
    };

    const canAccess = {
      dashboard: true,
      projects: true,
      calendar: true,
      gear: true,
      files: true,
      scenes: hasFilm,
      shots: hasFilm,
      team: true,
      settings: true,
    };

    const reason = {};
    if (!hasFilm) {
      reason.scenes = 'no_film_selected';
      reason.shots = 'no_film_selected';
    }

    return {
      to,
      canAccess,
      reason,
      // Convenience snapshot of the ids the builder used. Useful for
      // the debug overlay and for components that want to display the
      // current scope alongside the URL.
      currentWorkspaceId: wsId,
      currentFilmId: filmId,
      scope: hasFilm ? 'film' : hasWs ? 'workspace' : 'account',
    };
  }, [currentWorkspaceId, currentFilmId]);
}

export default useScopedNav;
